import { useSyncExternalStore } from "react";
import {
  createComment,
  deleteComment,
  editComment,
  fetchComment,
  fetchCommentContext,
  fetchCommentPage,
  fetchReplies,
  fetchRootComments,
  reactToComment,
  setCommentLike,
} from "../api/CommentAPI";

// In-session source of truth for comments (mirrors the iOS CommentsStore).
//
// Comments belong to a target — { type: "post" | "occurrence", id } (see
// CommentAPI). Post comments and occurrence comments are separate backend
// tables whose IDs can coincide, so every cache entry, list, expanded flag and
// in-flight guard is keyed by the target's type as well as the comment ID
// (commentKey): Post comment 17 and occurrence comment 17 never share state.
//
// Comments are stored flat by that key. Each paginated list (a target's root
// comments, or one comment's direct replies) is an ordered array of comment IDs
// with its own pagination state, in backend order (newest first). Components
// resolve IDs through the cache, so a like, edit, or delete shows up everywhere
// that comment is rendered — root list, reply list, focused conversation,
// ancestor context. Reply lists are only fetched when a branch is expanded;
// nothing here loads a whole tree.
//
// It lives at module level (read through useSyncExternalStore) so the cache and
// expanded branches survive moving between a post and its conversations
// without a provider in App.js.

export const DELETED_COMMENT_TEXT = "This comment was deleted.";
export const MAX_INLINE_LEVEL = 3;

export const postTarget = (postId) => ({ type: "post", id: Number(postId) });
export const occurrenceTarget = (occurrenceId) => ({ type: "occurrence", id: Number(occurrenceId) });

const targetKey = (target) => `${target.type}:${target.id}`;
// Comment IDs are unique per table, so the target type is enough to namespace them.
export const commentKey = (target, commentId) => `${target.type}:c${commentId}`;

// The target each list key belongs to, for fetching its first page. Recorded
// whenever a key is built, so any key a component holds can be loaded.
const listTargets = {};

export function rootsKey(target) {
  const key = `roots:${targetKey(target)}`;
  listTargets[key] = { target, commentId: null };
  return key;
}

export function repliesKey(target, commentId) {
  const key = `replies:${targetKey(target)}:${commentId}`;
  listTargets[key] = { target, commentId };
  return key;
}

const EMPTY_LIST = Object.freeze({
  ids: [],
  next: null,
  loaded: false,
  loading: false,
  error: false,
});

const initialState = () => ({
  byId: {},
  lists: {},
  expanded: new Set(),
  // Separate in-flight guards so a like and a reaction on the same comment
  // don't block each other.
  pendingLikes: new Set(),
  pendingReactions: new Set(),
});

let state = initialState();
let sessionToken = null;
// Bumped on reset so responses started under a previous session are dropped.
let epoch = 0;
// Per-list generation, bumped when a list is (re)loaded from its first page so a
// slower response for an older generation can't overwrite newer data.
let generations = {};
const listeners = new Set();

function update(changes) {
  state = { ...state, ...changes };
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function upsert(target, comments) {
  if (!comments.length) return;
  const byId = { ...state.byId };
  comments.forEach((c) => {
    byId[commentKey(target, c.id)] = c;
  });
  update({ byId });
}

function patchComment(target, id, fields) {
  const key = commentKey(target, id);
  const current = state.byId[key];
  if (!current) return;
  update({ byId: { ...state.byId, [key]: { ...current, ...fields } } });
}

function setList(key, fields) {
  const current = state.lists[key] || EMPTY_LIST;
  update({ lists: { ...state.lists, [key]: { ...current, ...fields } } });
}

function withSetItem(set, id, present) {
  const next = new Set(set);
  if (present) next.add(id);
  else next.delete(id);
  return next;
}

// Neutral placeholder: no author, text, or interaction state. reply_count is
// kept so the deleted comment's visible replies stay reachable.
function asDeleted(comment) {
  return {
    ...comment,
    is_deleted: true,
    text: DELETED_COMMENT_TEXT,
    user: null,
    user_id: null,
    profile_pic_url: null,
    likes_count: 0,
    is_liked: false,
    reaction_counts: {},
    my_reaction: null,
    mentions: [],
  };
}

function fetchFirstPage(key) {
  const { target, commentId } = listTargets[key];
  return commentId == null ? fetchRootComments(target) : fetchReplies(target, commentId);
}

async function load(key, nextUrl) {
  const { target } = listTargets[key];
  const replacing = !nextUrl;
  if (replacing) generations[key] = (generations[key] || 0) + 1;
  const generation = generations[key] || 0;
  const startEpoch = epoch;

  setList(key, { loading: true, error: false });
  try {
    const page = nextUrl ? await fetchCommentPage(nextUrl) : await fetchFirstPage(key);
    if (startEpoch !== epoch || generations[key] !== generation) return;

    const results = page.results || [];
    upsert(target, results);
    const previous = replacing ? [] : (state.lists[key] || EMPTY_LIST).ids;
    // Offset pagination shifts when comments are created, so a later page can
    // repeat items already shown — skip known IDs.
    const seen = new Set(previous);
    const ids = [...previous];
    results.forEach((c) => {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        ids.push(c.id);
      }
    });
    setList(key, { ids, next: page.next || null, loaded: true, loading: false, error: false });
  } catch (err) {
    if (startEpoch !== epoch || generations[key] !== generation) return;
    console.error("Failed to load comments", err);
    setList(key, { loading: false, error: true });
  }
}

// Drops every list whose key `matches`, bumping their generations so
// in-flight responses for them are discarded.
function withoutLists(matches) {
  const lists = { ...state.lists };
  Object.keys(lists).forEach((k) => {
    if (matches(k)) {
      generations[k] = (generations[k] || 0) + 1;
      delete lists[k];
    }
  });
  return lists;
}

/** Up to `limit` nearest ancestors of a cached comment of `target`, oldest
 *  first, or null if the comment or any needed ancestor isn't cached. */
export function cachedAncestors(byId, target, commentId, limit = 2) {
  let current = byId[commentKey(target, commentId)];
  if (!current) return null;
  const ancestors = [];
  while (ancestors.length < limit && current.parent != null) {
    const parent = byId[commentKey(target, current.parent)];
    if (!parent) return null;
    ancestors.unshift(parent);
    current = parent;
  }
  return ancestors;
}

export const commentsStore = {
  /** Clears everything when the signed-in token changes. Silent (no emit) so
   *  it's safe to call during render, before any comment component reads. */
  ensureSession(token) {
    if (token === sessionToken) return;
    sessionToken = token;
    epoch += 1;
    generations = {};
    state = initialState();
  },

  loadFirstPageIfNeeded(key) {
    const list = state.lists[key] || EMPTY_LIST;
    if (list.loaded || list.loading) return Promise.resolve();
    return load(key, null);
  },

  refresh(key) {
    return load(key, null);
  },

  /** Refreshes a target's root comments. On success its cached reply lists are
   *  dropped, so expanded branches reload lazily with fresh data. */
  async refreshTarget(target) {
    const key = rootsKey(target);
    await load(key, null);
    if (state.lists[key]?.error) return;
    const replies = `replies:${targetKey(target)}:`;
    update({ lists: withoutLists((k) => k.startsWith(replies)) });
  },

  /** Forgets everything cached for `target` — its lists, comments and
   *  expanded branches — e.g. once the viewer has lost access to it, so none
   *  of it can be shown again from the cache. */
  forgetTarget(target) {
    const prefix = `${target.type}:c`;
    const byId = {};
    Object.entries(state.byId).forEach(([key, comment]) => {
      const belongs =
        key.startsWith(prefix) &&
        Number(target.type === "post" ? comment.post : comment.occurrence) === target.id;
      if (!belongs) byId[key] = comment;
    });
    const roots = rootsKey(target);
    const replies = `replies:${targetKey(target)}:`;
    const lists = withoutLists((k) => k === roots || k.startsWith(replies));
    const expanded = new Set([...state.expanded].filter((key) => key in byId));
    update({ byId, lists, expanded });
  },

  loadNextPage(key) {
    const list = state.lists[key] || EMPTY_LIST;
    if (!list.next || list.loading) return Promise.resolve();
    return load(key, list.next);
  },

  setExpanded(target, commentId, expanded) {
    update({ expanded: withSetItem(state.expanded, commentKey(target, commentId), expanded) });
  },

  /** Ensures a comment and its nearest ancestors are cached, calling /context/
   *  only when something is missing (e.g. a direct link). Throws on failure. */
  async loadThread(target, commentId) {
    if (cachedAncestors(state.byId, target, commentId) !== null) return;
    await this.refreshThread(target, commentId);
  },

  /** Always fetches a comment and its nearest ancestors via /context/ and
   *  caches them. Used by notification links, where a cached copy may be
   *  stale. Throws on failure (404 = hidden by a block, or gone). */
  async refreshThread(target, commentId) {
    const startEpoch = epoch;
    const context = await fetchCommentContext(target, commentId);
    if (startEpoch !== epoch) return;
    upsert(target, [...(context.ancestors || []), context.focus]);
  },

  /** Creates a root comment (parentId null) or a reply and puts it at the top
   *  of its list if that list is loaded. Returns the target's new comment_count. */
  async createComment(target, text, parentId = null) {
    const data = await createComment(target, text, parentId);
    const comment = data.comment;
    upsert(target, [comment]);

    let key = rootsKey(target);
    if (parentId != null) {
      key = repliesKey(target, parentId);
      const parent = state.byId[commentKey(target, parentId)];
      if (parent) patchComment(target, parentId, { reply_count: (parent.reply_count || 0) + 1 });
      // Reveal the new reply. If the list isn't loaded yet, expanding fetches
      // a first page that will include it.
      commentsStore.setExpanded(target, parentId, true);
    }
    const list = state.lists[key];
    if (list?.loaded && !list.ids.includes(comment.id)) {
      setList(key, { ids: [comment.id, ...list.ids] });
    }
    return data.comment_count;
  },

  async editComment(target, comment, text) {
    const updated = await editComment(target, comment.id, text);
    upsert(target, [updated]);
  },

  /** Soft delete: the comment stays in every list as a placeholder so its
   *  replies remain reachable. Returns the new comment_count, if provided. */
  async deleteComment(target, comment) {
    const data = await deleteComment(target, comment.id);
    const key = commentKey(target, comment.id);
    const current = state.byId[key];
    if (current) update({ byId: { ...state.byId, [key]: asDeleted(current) } });
    return data?.comment_count;
  },

  /** Flips the like optimistically, then applies the server's state. On
   *  failure the pre-click state is restored and the error is rethrown.
   *  Clicks while a like request for this comment is in flight are ignored. */
  async toggleLike(target, commentId) {
    const key = commentKey(target, commentId);
    const comment = state.byId[key];
    if (!comment || comment.is_deleted || state.pendingLikes.has(key)) return;
    const startEpoch = epoch;
    update({ pendingLikes: withSetItem(state.pendingLikes, key, true) });

    const wasLiked = !!comment.is_liked;
    const wasCount = comment.likes_count || 0;
    const applyLike = (is_liked, likes_count) => {
      // A comment deleted meanwhile keeps its neutral placeholder state.
      if (startEpoch !== epoch || !state.byId[key] || state.byId[key].is_deleted) return;
      patchComment(target, commentId, { is_liked, likes_count });
    };

    applyLike(!wasLiked, Math.max(0, wasCount + (wasLiked ? -1 : 1)));
    try {
      const data = await setCommentLike(target, commentId, !wasLiked);
      applyLike(data.is_liked, data.likes_count);
    } catch (err) {
      applyLike(wasLiked, wasCount);
      throw err;
    } finally {
      if (startEpoch === epoch) {
        update({ pendingLikes: withSetItem(state.pendingLikes, key, false) });
      }
    }
  },

  /** Adds, switches, or (same type again) removes the viewer's reaction. Not
   *  optimistic, matching post reactions: the cache changes only on success. */
  async react(target, commentId, reactionType) {
    const key = commentKey(target, commentId);
    const comment = state.byId[key];
    if (!comment || comment.is_deleted || state.pendingReactions.has(key)) return;
    const startEpoch = epoch;
    update({ pendingReactions: withSetItem(state.pendingReactions, key, true) });
    try {
      const data = await reactToComment(target, commentId, reactionType, {
        isRemoval: comment.my_reaction === reactionType,
      });
      const current = state.byId[key];
      if (startEpoch === epoch && current && !current.is_deleted) {
        patchComment(target, commentId, {
          my_reaction: data.my_reaction ?? null,
          reaction_counts: data.reaction_counts || {},
        });
      }
    } finally {
      if (startEpoch === epoch) {
        update({ pendingReactions: withSetItem(state.pendingReactions, key, false) });
      }
    }
  },

  /** Re-fetches one comment, e.g. after an action was rejected because it was
   *  deleted meanwhile. Errors are ignored; the cached copy stays as is. */
  async reloadComment(target, commentId) {
    if (!state.byId[commentKey(target, commentId)]) return;
    const startEpoch = epoch;
    try {
      const fresh = await fetchComment(target, commentId);
      if (startEpoch === epoch) upsert(target, [fresh]);
    } catch {
      // Keep the cached copy.
    }
  },
};

// --- Hooks ---------------------------------------------------------------
// Selectors must return values that stay referentially stable between
// unrelated updates (cache entries, list objects, booleans).

export function useCommentsSelector(selector) {
  return useSyncExternalStore(subscribe, () => selector(state));
}

export const useComment = (target, id) =>
  useCommentsSelector((s) => (id == null ? undefined : s.byId[commentKey(target, id)]));
export const useCommentList = (key) => useCommentsSelector((s) => s.lists[key] || EMPTY_LIST);
export const useIsExpanded = (target, id) =>
  useCommentsSelector((s) => s.expanded.has(commentKey(target, id)));
export const useIsReactionPending = (target, id) =>
  useCommentsSelector((s) => s.pendingReactions.has(commentKey(target, id)));
